import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal } from '../../../../components';
import { routeKindCopy, routeKindOf } from '../../seo-copy';
import type { SiteSeoPage, SiteSeoRoute } from '../../seo-types';
import { routeName } from '../../route-tree';
import {
  Actions,
  ConfirmText,
  ErrorNote,
  Head,
  HeadText,
  KindChip,
  Note,
  Panels,
  Path,
  Root,
  Title,
  TitleLine,
} from './RouteEditor.styles';

export type RouteEditorProps = {
  /** null = Site defaults. */
  route: SiteSeoRoute | null;
  /** The site page behind the route, when there is one. */
  page?: SiteSeoPage;
  language: string;
  defaultLanguage: string;
  /** The server says the site no longer has this page (`page-missing`): its meta is not published. */
  missingPage?: boolean;
  /** The server's error on the route's path (e.g. `path-duplicate`), shown as is. */
  pathError?: string;
  /** Deletes the route (offered for the owner's own routes only, after a confirm). */
  onDelete?: () => void;
  /** The editor's panels (Search result, Social share, Footer SEO text, Advanced). */
  children: React.ReactNode;
};

/**
 * The selected route's editor: its header (label, path, kind, the dynamic-page and missing-page notes, Delete for the owner's own
 * routes) and the column the panels mount in.
 */
export const RouteEditor = ({
  route,
  page,
  language,
  defaultLanguage,
  missingPage = false,
  pathError,
  onDelete,
  children,
}: RouteEditorProps): React.ReactElement => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = React.useState(false);
  const deletable = route !== null && route.origin === 'custom' && onDelete !== undefined;
  const label = route ? routeName(route, page, language, defaultLanguage, t('Homepage')) : null;

  const confirmDelete = (): void => {
    setConfirming(false);
    if (deletable && onDelete) onDelete();
  };

  return (
    <Root aria-label={label ?? t('Site defaults')}>
      <Head>
        <HeadText>
          <TitleLine>
            {label === null ? <Title text="Site defaults" tag="h2" /> : <Title text={label} raw tag="h2" />}
            {route ? <KindChip text={routeKindCopy(t, routeKindOf(route))} raw tag="span" /> : null}
          </TitleLine>
          {route && label !== route.path ? <Path text={route.path} raw tag="span" /> : null}
          {route && route.match === 'pattern' && !page?.expanded ? (
            <Note text="Shown once the page loads; search engines and shares read it through prerender" tag="span" />
          ) : null}
          {route && missingPage ? (
            <Note
              text="This page no longer exists on your site; its meta is not published. Delete the route or pick another page."
              tag="span"
            />
          ) : null}
          {route && pathError ? <ErrorNote text={pathError} raw tag="span" /> : null}
        </HeadText>
        {deletable ? (
          <Button label={t('Delete route')} variant="outline" icon="trash" onClick={() => setConfirming(true)} />
        ) : null}
      </Head>
      <Panels>{children}</Panels>
      <Modal
        open={confirming}
        title={t('Delete route')}
        onClose={() => setConfirming(false)}
        footer={
          <Actions>
            <Button label={t('Cancel')} variant="outline" onClick={() => setConfirming(false)} />
            <Button label={t('Delete route')} onClick={confirmDelete} />
          </Actions>
        }
      >
        {route ? <ConfirmText text={route.path} raw tag="paragraph" /> : null}
      </Modal>
    </Root>
  );
};

export default RouteEditor;
